import { create } from '../../../utils/createStore.js';

/**
 * Field Sales dashboard filter selection.
 *
 * Only the LIVE filters are held here: multi-select Fiscal Year, Division,
 * State, Brand, HQ, BDM and ZM, plus multi-select Month. The backend accepts
 * comma-separated lists (empty list = no filter). Field Sales owns this
 * dedicated store so it never changes the shared single-value `useFilters`
 * store other dashboards use.
 *
 * Zone, Category and DSH are shown greyed in the filter bar (no data source
 * yet) and are intentionally NOT part of this store — they are never sent in
 * requests.
 *
 * Selections are "applied" explicitly (via the filter bar's Apply button), so
 * this store holds only the committed selection the hooks read from. The draft
 * (pre-Apply) selection lives locally in the filter bar component.
 *
 * NOTE: `months` are kept in the UI/URL shape "YYYY-MM" throughout the store;
 * FieldSalesApi converts each to the wire shape "YYYYMM" at the request
 * boundary.
 *
 * @typedef {Object} FieldSalesFilterSelection
 * @property {string[]} year - Fiscal years, e.g. "2026-2027".
 * @property {string[]} months - Months as YYYY-MM. Empty = backend default (latest).
 * @property {string[]} division
 * @property {string[]} state
 * @property {string[]} brand
 * @property {string[]} hq
 * @property {string[]} bdm
 * @property {string[]} zm
 */

/** @type {FieldSalesFilterSelection} */
const emptySelection = {
  year: [],
  months: [],
  division: [],
  state: [],
  brand: [],
  hq: [],
  bdm: [],
  zm: [],
};

/** Normalise one selection into a fully-populated, array-only shape. */
function normalise(selection = {}) {
  return {
    year: [...(selection.year ?? [])],
    months: [...(selection.months ?? [])],
    division: [...(selection.division ?? [])],
    state: [...(selection.state ?? [])],
    brand: [...(selection.brand ?? [])],
    hq: [...(selection.hq ?? [])],
    bdm: [...(selection.bdm ?? [])],
    zm: [...(selection.zm ?? [])],
  };
}

/**
 * Zustand store holding the APPLIED Field Sales filter selection.
 */
export const useFieldSalesFilters = create((set) => ({
  /** @type {FieldSalesFilterSelection} */
  applied: { ...emptySelection },

  /**
   * Commit a complete selection (called on Apply).
   * @param {FieldSalesFilterSelection} selection
   */
  applySelection: (selection) => set({ applied: normalise(selection) }),

  /** Reset applied selection to defaults (called on Reset). */
  resetSelection: () => set({ applied: { ...emptySelection } }),
}));

export { emptySelection as EMPTY_FIELD_SALES_SELECTION };
